import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import React from 'react';
import { styled } from '@mui/system';

dayjs.extend(duration);
dayjs.extend(relativeTime);

export const TASK_STATUS = {
  TODO: 'TODO',
  DONE: 'DONE',
  TERMINATED: 'TERMINATED',
  ERROR: 'ERROR',
};

export const STATUS_ARRAY = Object.values(TASK_STATUS);

// Define highlight colors - same as in SessionLogBox.jsx
const highlightColors = [
  'rgba(255, 255, 0, 0.7)', // bright yellow
  'rgba(50, 205, 50, 0.5)', // lime green
  'rgba(255, 105, 180, 0.5)', // hot pink
  'rgba(30, 144, 255, 0.5)', // dodger blue
  'rgba(255, 165, 0, 0.5)', // orange
  'rgba(147, 112, 219, 0.5)', // medium purple
  'rgba(0, 206, 209, 0.5)', // turquoise
  'rgba(255, 99, 71, 0.5)', // tomato red
  'rgba(152, 251, 152, 0.5)', // pale green
  'rgba(238, 130, 238, 0.5)', // violet
  'rgba(135, 206, 235, 0.5)', // sky blue
  'rgba(255, 218, 185, 0.7)', // peach
];

// Create styled mark component
const StyledMark = styled('mark')(({ colorIndex }) => ({
  backgroundColor: highlightColors[colorIndex % highlightColors.length],
  padding: '2px',
  borderRadius: '2px',
}));

export const generateTimeTracking = c => {
  let earliestStart = null;
  let latestEnd = null;
  let isWaitingState = false;

  let start = dayjs(c.startAt);
  let end = dayjs(c.endAt);
  let endStartDiff = end.diff(start);
  // Calculate the duration
  // Keep in mind you can get the duration in seconds, days, etc.

  if (endStartDiff < 0) {
    endStartDiff = endStartDiff * -1;

    //swith start and end
    let t = start;
    start = end;
    end = t;
  }

  if (c.status == 'WAITING' || c.status == 'PAUSED') {
    //the diff is from current time
    endStartDiff = dayjs().diff(start);
    isWaitingState = true;
  }

  if (endStartDiff < 1000) {
    //less than 1 second
    //round to 1 second
    endStartDiff = 1000;
  }

  c['timeTracking'] = endStartDiff;

  if (earliestStart == null) {
    //init
    earliestStart = start;
  } else {
    if (start.isBefore(earliestStart)) {
      earliestStart = start;
    }
  }

  if (latestEnd == null) {
    //init
    latestEnd = end;
  } else {
    if (end.isAfter(latestEnd)) {
      latestEnd = end;
    }
  }

  return c;
};

export const newLineToBreak = str => {
  return str.replace(/(?:\r\n|\r|\n)/g, '<br>');
};

export const breakToNewLine = str => {
  return str.replace(/<br\s*\/?>/gm, '\n');
};

export const stripHtml = html => {
  if (!html) return '';
  // First decode HTML entities (e.g., &lt; to <)
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  const decoded = txt.value;
  // Strip HTML tags but preserve <mark> tags for search highlighting
  // Replace mark tags with placeholders, strip other HTML, then restore mark tags
  const markPlaceholderOpen = '___MARK_OPEN___';
  const markPlaceholderClose = '___MARK_CLOSE___';
  const preserved = decoded
    .replace(/<mark>/gi, markPlaceholderOpen)
    .replace(/<\/mark>/gi, markPlaceholderClose);
  const stripped = preserved.replace(/(<([^>]+)>)/gi, '').trim();
  return stripped
    .replace(new RegExp(markPlaceholderOpen, 'g'), '<mark>')
    .replace(new RegExp(markPlaceholderClose, 'g'), '</mark>');
};

export const formatSearchHit = searchHit => {
  if (!searchHit) return '';

  // If there are no mark tags, return as is
  if (!searchHit.includes('<mark>')) return searchHit;

  // Split the string by <mark> tags and process each part
  const parts = searchHit.split(/<\/?mark>/g);

  let markCount = 0;
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <StyledMark key={index} colorIndex={markCount++}>
        {part}
      </StyledMark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};
